import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Option } from "react-bootstrap-typeahead/types/types";

//App
import {
  LookupValueContextData,
  LookupValueState,
} from "../../../../types/lookupValue";
import { SortBy } from "../../../../types";
import lookupValueApi from "../../../../app/model/api/lookupValueApi";
import {
  selectIsActive,
  selectSearchValue,
  setIsActive,
  setSearchValue,
} from "../../../../store/lookupvalue";
import { SearchValue } from "../../../../store/types";

//Common
import LoadingIndicator from "../../../common/VFX-UI/LoadingIndicator/LoadingIndicator";
import {
  useLookupValuesGridQuery,
  useLookupValueQuery,
} from "./hooks/useLookupValue";
import {
  useLookupTypeQuery,
  useLookupTypesQuery,
} from "../../../Admin/Lookup/LookupType/hooks/useLookupType";
import {
  useLookupGroupQuery,
  useLookupGroupsQuery,
} from "../../../Admin/Lookup/LookupGroup/hooks/useLookupGroup";
import { GRID_PAGE_SIZE } from "../../../../utility/constants";
import LookupValue from "./LookupValue";

export const LookupValueContext =
  React.createContext<LookupValueContextData | null>(null);

type LookupValueContainerProps = {
  lookupId: string;
};

const LookupValueContainer = (props: LookupValueContainerProps) => {
  const dispatch = useDispatch();
  const [state, setState] = useState<LookupValueState>({
    lookupTypeId: props.lookupId,
    lookupValueId: null,
    activeToggle: useSelector(selectIsActive) ?? true,
    searchPayload: useSelector(selectSearchValue) ?? null,
    currentPage: 1,
    pageSize: GRID_PAGE_SIZE,
    sortBy: [{ field: "name", direction: 1 }],
  });

  const lookupTypesQuery = useLookupTypesQuery({ isActive: true, excludeLocked: true }, true);
  const lookupGroupsQuery = useLookupGroupsQuery(state.lookupTypeId ?? null);
  const lookupValuesGridQuery = useLookupValuesGridQuery(state);
  const lookupValueQuery = useLookupValueQuery(state.lookupValueId ?? null);
  const lookupTypeQuery = useLookupTypeQuery(state.lookupTypeId ?? null);
  const lookupGroupQuery = useLookupGroupQuery(
    lookupValueQuery.data?.result.groupId
      ? lookupValueQuery.data?.result.groupId
      : null
  );

  const updateLookupValueId = async (lookupValueId: string | null) =>
    setState((prevState) => ({
      ...prevState,
      lookupValueId: lookupValueId,
    }));

  const updateLookupTypeId = async (lookupTypeId: string | null) =>
    setState((prevState) => ({
      ...prevState,
      searchPayload: null,
      lookupTypeId: lookupTypeId,
    }));

  const updateActiveToggle = async () => {
    dispatch(setIsActive());
    setState((prevState) => ({
      ...prevState,
      activeToggle: !prevState.activeToggle,
    }));
  };

  const updateSearchPayload = async (values: SearchValue | null) => {
    if (values?.name) {
      dispatch(setSearchValue({ name: values.name }));
    }
    if (values && Object.keys(values).length) {
      setState((prevState) => ({
        ...prevState,
        searchPayload: values,
        currentPage: 1,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        searchPayload: null,
        currentPage: 1,
        sortBy: [{ field: "name", direction: 1 }],
      }));
    }
  };

  const updateCurrentPage = async (currentPage: number) =>
    setState((prevState) => ({
      ...prevState,
      currentPage: currentPage,
    }));

  const updateCurrentPageSize = async (pageSize: number) => {
    setState((prevState) => ({
      ...prevState,
      pageSize: pageSize,
    }));
  };

  const updateSortBy = async (sortBy: SortBy) => {
    const { field, direction } = sortBy;
    setState((prevState) => ({
      ...prevState,
      sortBy: [{ field, direction }],
    }));
  };

  const { mutate: deleteMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await lookupValueApi.delete(value);
    },
  });

  const { mutate: activateMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await lookupValueApi.activate(value);
    },
  });

  const contextValue: LookupValueContextData = {
    data: lookupValueQuery?.data?.result,
    id: state.lookupValueId ?? null,
    lookupTypes: lookupTypesQuery?.data as Option[],
    lookupType: lookupTypeQuery?.data?.result,
    lookupGroups: lookupGroupsQuery?.data,
    lookupGroup: lookupGroupQuery?.data?.result,
    lookupTypeId: state.lookupTypeId ?? null,
    activeToggle: state.activeToggle,
    searchValue: state.searchPayload ?? undefined,
    paging: {
      currentPage: state.currentPage ?? 1,
      totalSize: lookupValuesGridQuery?.data?.totalSize ?? 0,
    },
    pageSize: state.pageSize,
    setId: updateLookupValueId,
    setLookupTypeId: updateLookupTypeId,
    setActiveToggleValue: updateActiveToggle,
    setSearchValue: updateSearchPayload,
    setCurrentPageSize: updateCurrentPageSize,
    setCurrentPage: updateCurrentPage,
    setSortBy: updateSortBy,
    refetchGrid: () => lookupValuesGridQuery?.refetch(),
  };

  if (lookupTypesQuery?.isLoading) {
    return <LoadingIndicator />;
  }

  return (
    <LookupValueContext.Provider value={contextValue}>
      <LookupValue
        data={lookupValuesGridQuery?.data?.result}
        deleteMutation={deleteMutation}
        activateMutation={activateMutation}
      />
    </LookupValueContext.Provider>
  );
};

export default LookupValueContainer;
